// Ported from obsidian-plugin/src/components/MessageList.tsx.
// Changes:
//   - <EmptyState> is inlined here (no separate file) and its /stats fetch
//     hits Django's generic proxy at /api/fastapi/stats instead of a
//     hard-coded base URL.
//   - VaultRef props removed — no Obsidian vault in the browser.
import React, { useEffect, useMemo, useRef } from 'react';
import { Message } from './Message';
import { RetrievalCard } from './RetrievalCard';
import { useEntityIndex } from './useEntityIndex';
import { useTheme } from '../../contexts/ThemeContext';
import type { Stats } from './useLlmStatus';
import malpediaLogo from '../../assets/malpedia-logo.png';
import malpediaLogoDark from '../../assets/malpedia-logo-dark.png';
import type {
  SourceDoc,
  PipelineStep,
  PlanningEvent,
  RoutingEvent,
  StepDetail,
  GraphTraversalEvent,
  ProfileEvent,
  ScoreThreshold,
  DetectedIOC,
  EntityLink,
} from '../../types';

export interface MessageData {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceDoc[];
  streaming?: boolean;
  progressStep?: PipelineStep | null;
  progressMessage?: string;
  progressCount?: number;
  completedSteps?: PipelineStep[];
  stepDetails?: StepDetail[];
  planningEvent?: PlanningEvent | null;
  routingEvent?: RoutingEvent | null;
  graphTraversalEvent?: GraphTraversalEvent | null;
  profileEvent?: ProfileEvent | null;
  scoreThreshold?: ScoreThreshold | null;
  planningTokens?: string;
  queryDetectedIocs?: DetectedIOC[];
  responseDetectedIocs?: DetectedIOC[];
  turnId?: string;          // server-issued; absent in temporary chats
}

interface EmptyStateProps {
  onSuggestionClick: (query: string) => void;
  stats: Stats | null;
}

const SUGGESTIONS = [
  'What TTPs does Lazarus Group use?',
  'Recent reports mentioning Cobalt Strike',
  'Compare APT28 and APT29 tooling',
];

function EmptyState({ onSuggestionClick, stats }: EmptyStateProps) {
  const { resolved } = useTheme();

  return (
    <div className="malpedia-empty-state">
      <div className="malpedia-empty-state-icon">
        <img
          src={resolved === 'dark' ? malpediaLogoDark : malpediaLogo}
          alt="Malpedia"
          className="malpedia-empty-state-logo"
        />
      </div>
      <div className="malpedia-empty-state-title">MalpediaRAG</div>
      <div className="malpedia-empty-state-subtitle">
        Threat intelligence at your fingertips
      </div>

      <div className="malpedia-empty-state-stats">
        <div className="malpedia-empty-state-stat">
          <span className="malpedia-empty-state-stat-value">
            {stats?.docs?.toLocaleString() ?? '—'}
          </span>
          <span className="malpedia-empty-state-stat-label">Docs</span>
        </div>
        <div className="malpedia-empty-state-stat">
          <span className="malpedia-empty-state-stat-value">
            {stats?.actors?.toLocaleString() ?? '—'}
          </span>
          <span className="malpedia-empty-state-stat-label">Actors</span>
        </div>
        <div className="malpedia-empty-state-stat">
          <span className="malpedia-empty-state-stat-value">
            {stats?.families?.toLocaleString() ?? '—'}
          </span>
          <span className="malpedia-empty-state-stat-label">Families</span>
        </div>
      </div>

      {stats?.llm_status && stats.llm_status !== 'ok' && (
        <div
          className="malpedia-empty-state-llm-warning"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            marginBottom: '1rem',
            borderRadius: '0.5rem',
            backgroundColor: 'var(--background-modifier-warning, #fff3cd)',
            color: 'var(--text-warning, #856404)',
            fontSize: '0.85rem',
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>&#x26A0;</span>
          <span>
            {stats.llm_status === 'not_configured'
              ? 'No LLM backend configured — chat will not work.'
              : `LLM unavailable: ${stats.llm_status.replace(/^unreachable:\s*/, '')}`}
          </span>
        </div>
      )}

      <div className="malpedia-empty-state-suggestions">
        {SUGGESTIONS.map((q) => (
          <div
            key={q}
            className="malpedia-empty-state-suggestion"
            onClick={() => onSuggestionClick(q)}
          >
            <span className="malpedia-empty-state-suggestion-arrow">→</span>
            {q}
          </div>
        ))}
      </div>
    </div>
  );
}

interface MessageListProps {
  messages: MessageData[];
  onSuggestionClick?: (query: string) => void;
  stats?: Stats | null;
}

// Union the corpus-wide index with the retrieval-scoped entities surfaced
// for this turn. Retrieval entries win on conflict — they carry the
// scoring/strategy info the static index doesn't have.
function mergeEntityLinks(
  corpus: EntityLink[],
  graph: GraphTraversalEvent | null | undefined,
): EntityLink[] | undefined {
  const retrieval: EntityLink[] = graph
    ? graph.nodes
        .filter(
          (n): n is typeof n & { url: string } =>
            (n.label === 'MalwareFamily' || n.label === 'ThreatActor') && !!n.url,
        )
        .map((n) => ({ name: n.name, label: n.label, url: n.url }))
    : [];

  if (corpus.length === 0 && retrieval.length === 0) return undefined;

  const byKey = new Map<string, EntityLink>();
  for (const link of corpus) {
    byKey.set(link.name.toLowerCase(), link);
  }
  for (const link of retrieval) {
    // Preserve aliases from the corpus entry if retrieval supplied none.
    const key = link.name.toLowerCase();
    const existing = byKey.get(key);
    if (existing?.aliases && !link.aliases) {
      byKey.set(key, { ...link, aliases: existing.aliases });
    } else {
      byKey.set(key, link);
    }
  }
  return [...byKey.values()];
}

export function MessageList({ messages, onSuggestionClick, stats }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const entityIndex = useEntityIndex();

  // Stable corpus-wide entity list — actors + families, each carrying its
  // aliases. Fed to every assistant <Message> so the linkifier can mark
  // names mentioned in prose even when retrieval didn't surface them.
  const corpusEntities: EntityLink[] = useMemo(() => {
    if (!entityIndex) return [];
    return [...entityIndex.actors, ...entityIndex.families];
  }, [entityIndex]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (messages.length === 0) {
    return <EmptyState onSuggestionClick={onSuggestionClick || (() => {})} stats={stats ?? null} />;
  }

  const lastMsg = messages[messages.length - 1];
  const isThinking =
    lastMsg?.role === 'assistant' && lastMsg.streaming && !lastMsg.content;

  return (
    <div className="malpedia-message-list">
      {messages.map((msg) => (
        <React.Fragment key={msg.id}>
          {msg.role === 'assistant' && (
            <RetrievalCard
              streaming={msg.streaming}
              hasContent={!!msg.content}
              progressStep={msg.progressStep}
              progressMessage={msg.progressMessage}
              progressCount={msg.progressCount}
              completedSteps={msg.completedSteps}
              stepDetails={msg.stepDetails}
              planningEvent={msg.planningEvent}
              routingEvent={msg.routingEvent}
              graphTraversalEvent={msg.graphTraversalEvent}
              profileEvent={msg.profileEvent}
              scoreThreshold={msg.scoreThreshold}
              sources={msg.sources}
              planningTokens={msg.planningTokens}
            />
          )}
          {!(msg.id === lastMsg?.id && isThinking) && (
            <Message
              role={msg.role}
              content={msg.content}
              sources={msg.sources}
              streaming={msg.streaming}
              completedSteps={msg.completedSteps}
              stepDetails={msg.stepDetails}
              planningEvent={msg.planningEvent}
              routingEvent={msg.routingEvent}
              profileEvent={msg.profileEvent}
              scoreThreshold={msg.scoreThreshold}
              detectedIocs={
                msg.role === 'user' ? msg.queryDetectedIocs : msg.responseDetectedIocs
              }
              entityLinks={
                msg.role === 'assistant'
                  ? mergeEntityLinks(corpusEntities, msg.graphTraversalEvent)
                  : undefined
              }
              turnId={msg.role === 'assistant' ? msg.turnId : undefined}
            />
          )}
        </React.Fragment>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
